import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  EventEmitter,
  input,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from "@angular/core";
import { NgClass, NgIf, NgStyle } from "@angular/common";
import { NoDataFoundComponent } from "../no-data-found/no-data-found.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ResizableModule, ResizeEvent } from "angular-resizable-element";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { TableMenuComponent } from "../table-menu/table-menu.component";
import { StatsData } from "../../models/stats-response.interface";
import { TableMenuOption, TableMeta } from "../../models/table-meta.interface";
import { PaginatedListDataResponse } from "../../models/paginated-list-data-response";
import {
  FilterCondition,
  FilterConfig,
  SearchBarConfig,
  TableListRequest,
  ViewConfig,
  ViewItem,
  Where
} from "../../models/table-list-request.interface";
import { User } from "../../models/user.interface";
import { TosTableTdComponent } from "./tos-table-td/tos-table-td.component";
import { LoadingService } from "../../services/loading.service";
import { SkeletonLoaderComponent } from "../skeleton-loader/skeleton-loader.component";
import { RightPanelComponent } from "../right-panel/right-panel.component";
import { AdvancedFilterComponentV2 } from "../advanced-filter/advanced-filter-v2.component";
import { AdvancedFilterFieldInfo } from "../../models/advanced-filter-config.interface";
import { FieldInfo } from "../../models/editable-table-header.interface";
import { CommonUtils } from "../../utils/common-utils";
import { NoDataComponent } from "../no-data/no-data.component";
import { Subject, takeUntil } from "rxjs";
import { MultiSelectComponent } from "../select/multi-select.component";


@Component({
  selector: "app-tos-table",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NoDataFoundComponent,
    FormsModule,
    ReactiveFormsModule,
    ResizableModule,
    SvgIconComponent,
    TableMenuComponent,
    NgStyle,
    NgClass,
    SkeletonLoaderComponent,
    NoDataComponent,
    TosTableTdComponent,
    MultiSelectComponent
  ],
  templateUrl: "./tos-table.component.html",
  styleUrl: "./tos-table.component.scss"
})
export class TosTableComponent implements AfterViewInit, OnInit, OnChanges {
  inputWidth = 5;
  totalPages: number = 0;
  openMenuIndex: number | null = null;
  customPageInput: number = 1;
  debounceTimer: any = null;
  isLoading: boolean = false;
  // Filter And Search states
  searchTerm: any = "";
  previousTerm: string = "";
  rawFilterData: Where | null = null;
  isFilterApplied: boolean = false;
  filterData: Where | null = null;
  searchFilterData!: Where | null;

  @Input() selectedItems: any[] = [];
  @Input() stats!: StatsData;
  @Input() tableMeta!: TableMeta;
  @Input() dataListRequest!: TableListRequest;
  @Input() dataListResponse!: PaginatedListDataResponse;
  @Input() showTableStats: boolean = true;
  @Input() tableMenuOptions: TableMenuOption[] = [];
  @Input() showMenuOptions: boolean = true;
  @Input() allTableRecordsSelected: boolean = false;
  @Input() searchBarConfig?: SearchBarConfig;
  @Input() filterConfig?: FilterConfig
  @Input() defaultFilters?: Where;
  @Input() viewFilters?: Where;
  @Input() viewConfig?: ViewConfig;

  @Output() dataListRequestChange = new EventEmitter<TableListRequest>();
  @Output() tosTableAction = new EventEmitter<any>();
  @Output() rowActionTriggered = new EventEmitter<any>();
  @Output() selectedItemsChange = new EventEmitter<any[]>();
  @Output() allTableRecordsSelectedChange = new EventEmitter<void>();
  @Output() tableMenuOptionSelected = new EventEmitter<any>();
  @Output() onViewChange = new EventEmitter<ViewItem>();

  @ViewChild("widthCalculator") widthCalculator!: ElementRef;

  @ViewChild('viewselector', { static: false }) viewSelector!: MultiSelectComponent;

  constructor(private cdr: ChangeDetectorRef,
              private loadingService: LoadingService,
              private viewContainerRef: ViewContainerRef) {
  }

  ngOnInit() {
    this.loadingService.loading$
      .subscribe({
        next: data => {
          this.isLoading = data.isLoading && data.type === "inline";
          this.cdr.detectChanges();
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  ngAfterViewInit() {
    this.updateInputWidth();
    this.setPageFilters();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["dataListResponse"] &&
      changes["dataListResponse"].currentValue
    ) {
      this.calculateTotalPages();
      this.customPageInput = this.dataListRequest.page + 1;
    }
  }

  handlePreviousBtnClick() {
    if (!(this.dataListRequest.page > 0)) return;

    this.dataListRequest.page -= 1;
    this.tosTableAction.emit();
  }

  handleNextButtonClick() {
    if (
      !(
        this.dataListRequest.page >= 0 &&
        this.dataListRequest.page < this.totalPages - 1
      )
    )
      return;

    this.dataListRequest.page += 1;
    this.tosTableAction.emit();
  }

  handleRowsPerPage(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    const selectedValue = selectElement.value;
    this.dataListRequest.limit = Number(selectedValue);
    this.dataListRequest.page = 0;
    this.tosTableAction.emit();
  }

  onResizeEnd(event: ResizeEvent, column: string): void {
    if (event.edges.right) {
      // Use attribute selectors to handle column names with dots
      const columnHeader = document.querySelector(
        `th[class*="${column}_th"]`
      ) as HTMLElement;
      const columnCells = document.querySelectorAll(
        `td[class*="${column}_td"]`
      );
      const newWidth = `${event.rectangle.width}px`;

      if (columnHeader) {
        columnHeader.style.width = newWidth;
      }

      columnCells.forEach((cell) => {
        const cellHtml = cell as HTMLElement;
        cellHtml.style.width = newWidth;
      });
    }
  }

  sortData(column: string) {
    if (!this.dataListRequest.orderBy) {
      this.dataListRequest.orderBy = {};
    }

    const currentOrder = this.dataListRequest.orderBy[column];

    if (!currentOrder) {
      this.dataListRequest.orderBy = {
        [column]: "desc",
        ...this.dataListRequest.orderBy
      };
    } else if (currentOrder === "desc") {
      this.dataListRequest.orderBy[column] = "asc";
    } else if (currentOrder === "asc") {
      delete this.dataListRequest.orderBy[column];
    }

    this.tosTableAction.emit();
  }

  calculateTotalPages() {
    if (this.dataListResponse.totalCount) {
      this.totalPages = Math.ceil(
        this.dataListResponse.totalCount / this.dataListRequest.limit
      );
    } else if (this.stats) {
      this.totalPages = Math.ceil(
        this.stats.total / this.dataListRequest.limit
      );
    }
  }

  orderByListContainsField(fieldName: string): any {
    return (
      this.dataListRequest.orderBy &&
      this.dataListRequest.orderBy.hasOwnProperty(fieldName)
    );
  }

  toggleSelection(record: any) {
    if (!this.isSelected(record)) {
      this.selectedItems.push(record);
    } else {
      const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
      this.selectedItems = this.selectedItems.filter((i) =>
        uniqueColumns.length > 0
          ? !uniqueColumns.every((key) => i?.[key] === record?.[key])
          : i?.id !== record?.id
      );
      if (this.allTableRecordsSelected) {
        this.allTableRecordsSelected = false;
        this.allTableRecordsSelectedChange.emit();
      }
    }
    this.selectedItemsChange.emit(this.selectedItems);
  }

  isSelected(item: any): boolean {
    if (this.allTableRecordsSelected) {
      return true;
    }

    const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
    if (uniqueColumns.length > 0) {
      return this.selectedItems.some((x) =>
        uniqueColumns.every((key) => x[key] === item[key])
      );
    }

    return this.selectedItems.some((x) => x.id === item.id);
  }

  isAllRecordsOfCurrentPageSelected(): boolean {
    return (
      this.allTableRecordsSelected ||
      (this.dataListResponse.records?.length > 0 &&
        this.dataListResponse.records?.every((item: User) =>
          this.isSelected(item)
        ))
    );
  }

  toggleAllRecordsOfCurrentPageSelections(isChecked: boolean) {
    if (isChecked) {
      this.dataListResponse.records?.forEach((item) => {
        if (!this.isSelected(item)) {
          this.selectedItems.push(item);
        }
      });
    } else {
      const uniqueColumns = this.tableMeta?.uniqueColumns ?? [];
      this.dataListResponse.records?.forEach((item) => {
        this.selectedItems = this.selectedItems.filter((i) =>
          uniqueColumns.length > 0
            ? !uniqueColumns.every((key) => i?.[key] === item?.[key])
            : i?.id !== item?.id
        );
      });
    }

    this.selectedItemsChange.emit(this.selectedItems);
  }

  handleMenuToggle(index: number) {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  handleOptionSelected(record: any, actionName: any) {
    this.tableMenuOptionSelected.emit({ record, actionName });
  }

  handleInlineAction(event: any) {
    this.rowActionTriggered.emit(event);
  }

  getNestedValue(record: any, fieldName: string): any {
    if (!fieldName.includes(".")) {
      return record[fieldName];
    }
    return fieldName.split(".").reduce((acc, part) => acc && acc[part], record);
  }

  protected readonly input = input;


  onDebouncedPageInputChange(): void {
    //basic debounced used so that does make api does not call at every change
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      const page = Number(this.customPageInput);

      if (!isNaN(page) && page >= 1 && page <= this.totalPages) {
        this.dataListRequest.page = page - 1;
        this.tosTableAction.emit();
      } else {
        this.customPageInput = this.dataListRequest.page + 1; // reset to valid value
      }
    }, 300);
    this.updateInputWidth();
  }

  updateInputWidth() {
    //This Function is used for dynamic input size increasing width of pagination input box this i made because was not using ngModel
    if (this.widthCalculator) {
      const spanWidth = this.widthCalculator.nativeElement.offsetWidth;
      this.inputWidth = Math.max(30, spanWidth + 10); // padding
    }
  }

  handleTableSearch(event: KeyboardEvent) {
    const searchTerm = this.searchTerm.trim();

    // this.searchFilterData = this.searchBarConfig?.filters

    if (searchTerm.length > 0) {
      const filterConditions = this.getFilterConditionsFromSearchBarConfig(searchTerm);
      const expression = this.getExpressionForConditions(filterConditions, "OR");
      this.searchFilterData = {
        filterConditions,
        expression
      };

      if (event.key === "Enter") {
        this.mergeFilters();
      }
    }

    if (searchTerm.length === 0 && this.previousTerm.length !== 0) {
      this.searchFilterData = null;
      this.mergeFilters();
    }
    this.previousTerm = searchTerm;
  }

  filter() {
    const rightPanelComponentComponentRef = this.viewContainerRef.createComponent(RightPanelComponent);
    rightPanelComponentComponentRef.instance.title = "Advanced Filter";
    rightPanelComponentComponentRef.instance.showActionBtns = true;
    rightPanelComponentComponentRef.instance.primaryActionBtnText = "Apply Filter";


    const fieldInfoList: FieldInfo[] = CommonUtils.getFilterFieldList(this.filterConfig?.filterFields ?? []);
    const filterDialogRef: ComponentRef<AdvancedFilterComponentV2> = rightPanelComponentComponentRef.instance.loadComponent(
      AdvancedFilterComponentV2, {
        filterFields: fieldInfoList,
        appliedFilters: this.rawFilterData,
        useNewContract: this.filterConfig?.useNewContract ?? false,
        view: "panelView"
      }
    );

    rightPanelComponentComponentRef.instance.primaryActionBtnEmitter.subscribe(action => {
      filterDialogRef.instance.applyFilter();
    });

    rightPanelComponentComponentRef.instance.close.subscribe((res: any) => {
      this.selectedItems = [];
      if (res && res.resetAll) {
        this.rawFilterData = null;
        this.filterData = null;
        this.isFilterApplied = false;
        this.mergeFilters();
      } else if (res && res.refresh) {
        if (res.filterData.expression && res.filterData.filterConditions.length > 0) {
          this.filterData = {
            filterConditions: res.filterData.filterConditions,
            expression: res.filterData.expression
          };
          this.isFilterApplied = true;
          this.rawFilterData = res.rawFilterData;
          this.mergeFilters();
        } else {
          console.log("Invalid filter !");
          this.rawFilterData = null;
          this.filterData = null;
          this.isFilterApplied = false;
          this.mergeFilters();
        }
      }

      rightPanelComponentComponentRef.destroy();
    });

  }

  mergeFilters() {
    this.dataListRequest.where = this.defaultFilters ?? { filterConditions: [], expression: "" };

    if (this.filterData?.filterConditions?.length) {
      this.filterData.filterConditions.forEach((filter: any) => {
        this.dataListRequest.where?.filterConditions?.push(filter);
      });
    }

    if (this.searchFilterData?.filterConditions?.length) {
      this.searchFilterData.filterConditions.forEach((filter: any) => {
        this.dataListRequest.where?.filterConditions?.push(filter);
      });
    }

    if (this.viewFilters?.filterConditions?.length) {
      this.viewFilters.filterConditions.forEach((filter: any) => {
        this.dataListRequest.where?.filterConditions?.push(filter);
      });
    }

    const expressions: string[] = [];
    if (this.filterData?.expression) {
      expressions.push(`${this.filterData.expression}`);
    }
    if (this.searchFilterData?.expression) {
      expressions.push(`(${this.searchFilterData.expression})`);
    }
    if(this.defaultFilters?.expression){
      expressions.push(`(${this.defaultFilters?.expression})`);
    }
    if(this.viewFilters?.expression){
      expressions.push(`(${this.viewFilters?.expression})`);
    }

    // Safely assign the joined expression
    this.dataListRequest.where.expression = expressions.join(" AND ");
    this.dataListRequest.page = 0; //reset to page 0
    this.tosTableAction.emit();
  }

  private getExpressionForConditions(filterConditions: FilterCondition[], joinString: "OR" | "AND"): string {
    return filterConditions
      .map(config => config.alias)
      .join(` ${joinString} `);
  }

  private getFilterConditionsFromSearchBarConfig(searchTerm: string): FilterCondition[] {
    return this.searchBarConfig?.filters.map((config): FilterCondition => {
      return {
        ...config,
        value: [searchTerm]
      };
    }) ?? [];
  }

  private setPageFilters(): void {
    if (this.viewConfig?.viewOptions) {
      this.viewSelector.toggleSelection(this.viewConfig.viewOptions[0]);
    } else {
      this.mergeFilters();
    }
  }

  handleViewChange(selectedView: ViewItem): void {
    const filterConditions = selectedView.filters;
    const expression = this.getExpressionForConditions(filterConditions, "AND");
    this.viewFilters = {
      filterConditions,
      expression
    };
    this.mergeFilters();
    this.onViewChange.emit(selectedView);
  }
}
